import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
  TablePagination,
  IconButton,
  Collapse,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import axios from "../../utils/axiosInstance";

//for pdf Export
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
//for excel Export
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PRC_WELL } from "../../utils/PRC_WELL";
import { WellProCells } from "./wellProCells";
import { C5Cells } from "./getC5Cells";
import { formatDate } from "../../utils/formatDate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
export default function ControllerStatus() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [controllers, setControllers] = useState([]);
  const [loadingController, setLoadingController] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [sdData, setSdData] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [expandedRows, setExpandedRows] = useState({});
  
  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const fetchControllers = async () => {
    try {
      setLoadingController(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/controller-status/SHUTDOWN_ENUM`
      );

      setControllers(response.data.docs || []);
      // setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching controllers:", err);
    } finally {
      setLoadingController(false);
    }
  };

  const gotoUnitPage = (id) => {
    navigate(`../device/${id}/engine-data`);
  };

  useEffect(() => {
    fetchControllers();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await PRC_WELL.getSDAsync();
      setSdData(data);
    })();
  }, []);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const copyToClipboard = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No controllers data to copy");
      return;
    }

    const headers =
      "Unit Name\tModem Serial Number\tNetwork\tController\tPlungers\tSD Code\tShutdown Enum\tSample Time";

    const lines = sortedData.map((c) => {
      const controllerName =
        c.type === "WellPro II" ? c.type : c.controller || "-";

      const plungers =
        c.type === "C5" || c.type === "C5E" ? c.numPlungers : "-";

      const sdCode = c.value; // both C5 and WellPro II share value

      let sdEnumStatus = "";
      if (c.value === 0) sdEnumStatus = "Normal Operation";
      else if (c.value === -1) sdEnumStatus = "MODBUS";
      else if (c.value > 0) {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      } else {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      }

      const sampleTime = formatDate(c.time * 1000);

      return `${c.name}\t${c.unitId}\t${c.interface}\t${controllerName}\t${plungers}\t${sdCode}\t${sdEnumStatus}\t${sampleTime}`;
    });

    const finalText = `${headers}\n${lines.join("\n")}`;

    navigator.clipboard
      .writeText(finalText)
      .then(() => toast.success("Controller Status copied to clipboard"))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const exportToCSV = () => {
    if (!sortedData || sortedData.length === 0) {
      console.warn("No controllers to export");
      return;
    }
    const csvContent = [
      [
        "Unit Name",
        "Modem Serial Number",
        "Network",
        "Controller",
        "Plungers",
        "SD Code",
        "Shutdown Enum",
        "Sample Time",
      ],
      ...sortedData.map((c) => {
        const controllerName =
          c.type === "WellPro II" ? c.type : c.controller || "-";

        const plungers =
          c.type === "C5" || c.type === "C5E" ? c.numPlungers : "-";

        const sdCode = c.value;

        let sdEnumStatus = "";
        if (c.value === 0) sdEnumStatus = "Normal Operation";
        else if (c.value === -1) sdEnumStatus = "MODBUS";
        else if (c.value > 0) {
          if (c.type === "C5" || c.type === "C5E") {
            sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
          } else {
            sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
          }
        } else {
          if (c.type === "C5" || c.type === "C5E") {
            sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
          } else {
            sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
          }
        }

        const sampleTime = formatDate(c.time * 1000);
        return [
          c.name,
          c.unitId,
          c.interface,
          controllerName,
          plungers,
          sdCode,
          sdEnumStatus,
          sampleTime,
        ];
      }),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Controller_Status.csv`;
    link.click();

    handleExportMenuClose();
    toast.success("Exported Controllers Status view to CSV");
  };

  const exportToExcel = () => {
    if (!controllers || controllers.length === 0) return;
    let headers, rows;

    headers = [
      "Unit Name",
      "Modem Serial Number",
      "Network",
      "Controller",
      "Plungers",
      "SD Code",
      "Shutdown Enum",
      "Sample Time",
    ];
    rows = sortedData.map((c) => {
      const controllerName =
        c.type === "WellPro II" ? c.type : c.controller || "-";

      const plungers =
        c.type === "C5" || c.type === "C5E" ? c.numPlungers : "-";

      const sdCode = c.value;

      let sdEnumStatus = "";
      if (c.value === 0) sdEnumStatus = "Normal Operation";
      else if (c.value === -1) sdEnumStatus = "MODBUS";
      else if (c.value > 0) {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      } else {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      }

      const sampleTime = formatDate(c.time * 1000);
      return [
        c.name,
        c.unitId,
        c.interface,
        controllerName,
        plungers,
        sdCode,
        sdEnumStatus,
        sampleTime,
      ];
    });

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Units");

    // Export file
    XLSX.writeFile(workbook, `controller_Status_data.xlsx`);

    handleExportMenuClose();
    toast.success(`Exported Controller Status view to Excel`);
  };

  const exportToPDF = () => {
    if (!controllers || controllers.length === 0) return;

    const doc = new jsPDF("l", "pt", "a4");

    doc.setFontSize(10);
    doc.text("Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Modem Serial Number",
        "Network",
        "Controller",
        "Plungers",
        "SD Code",
        "Shutdown Enum",
        "Sample Time",
      ],
    ];

    const rows = sortedData.map((c) => {
      const controllerName =
        c.type === "WellPro II" ? c.type : c.controller || "-";
      const plungers =
        c.type === "C5" || c.type === "C5E" ? c.numPlungers : "-";

      const sdCode = c.value;

      let sdEnumStatus = "";
      if (c.value === 0) sdEnumStatus = "Normal Operation";
      else if (c.value === -1) sdEnumStatus = "MODBUS";
      else if (c.value > 0) {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      } else {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      }

      const sampleTime = formatDate(c.time * 1000);

      return [
        c.name,
        c.unitId,
        c.interface,
        controllerName,
        plungers,
        sdCode,
        sdEnumStatus,
        sampleTime,
      ];
    });

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
    });

    doc.save(`Controller_Status.pdf`);
    handleExportMenuClose();
    toast.success(`Exported Controllers Status PDF`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // 1 sort
  const sortedData = useMemo(() => {
    if (!controllers) return [];
    return [...controllers].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Unit Name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "Modem Serial Number":
          aValue = a.unitId;
          bValue = b.unitId;
          break;
        case "Network":
          aValue = a.interface;
          bValue = b.interface;
          break;
        case "Controller":
          aValue = a.controller;
          bValue = b.controller;
          break;
        case "Plungers":
          aValue = a.numPlungers;
          bValue = b.numPlungers;
          break;
        case "SD Code":
          aValue = a.value;
          bValue = b.value;
          break;
        case "Shutdown Enum":
          aValue = a.value;
          bValue = b.value;
          break;
        case "Sample Time":
          aValue = Number(a.time);
          bValue = Number(b.time);
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && aValue !== null) {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === "string" && bValue !== null) {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [controllers, order, orderBy]);

  // 2 normalize
  const normalizedControllers = useMemo(() => {
    if (!sdData) return [];
    return sortedData.map((c) => {
      const controllerName =
        c.type === "WellPro II" ? c.type : c.controller || "-";
      const plungers =
        c.type === "C5" || c.type === "C5E" ? c.numPlungers : "-";
      const sdCode = c.value;
      let sdEnumStatus = "-";
      if (c.value === 0) sdEnumStatus = "Normal Operation";
      else if (c.value === -1) sdEnumStatus = "MODBUS";
      else if (c.value > 0) {
        if (c.type === "C5" || c.type === "C5E") {
          sdEnumStatus = sdData?.C5?.[c.value] ?? "-";
        } else {
          sdEnumStatus = sdData?.WP?.[c.value] ?? "-";
        }
      }
      const sampleTime = formatDate(c.time * 1000);
      return {
        ...c,
        controllerName,
        plungers: String(plungers),
        sdCode: String(sdCode),
        sdEnumStatus,
        sampleTime,
      };
    });
  }, [sortedData, sdData]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, controllers]);

  // 3 filter - this must apply on WHOLE normalizedControllers
  const filteredControllers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return normalizedControllers.filter((item) =>
      Object.values(item).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [normalizedControllers, searchQuery]);

  // 4 pagination now
  const paginatedUnits = useMemo(() => {
    return filteredControllers.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredControllers, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderTable = () => (
  <TableContainer 
    sx={{ 
      flexGrow: 1, 
      overflow: "auto", 
      height: { xs: "50vh", md: "calc(100vh - 350px)" },
      WebkitOverflowScrolling: "touch",
      overscrollBehaviorY: "none",
    }}
  >
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
          {/* Mobile expand button column */}
          <TableCell
            sx={{
              display: { xs: "table-cell", md: "none" },
              fontWeight: "bold",
              textAlign: "center",
              fontSize: "12px",
              padding: "12px 8px",
              width: "50px",
            }}
          >
            Details
          </TableCell>

          <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Unit Name"}
              direction={orderBy === "Unit Name" ? order : "asc"}
              onClick={() => handleRequestSort("Unit Name")}
            >
              Unit Name
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Modem Serial Number"}
              direction={orderBy === "Modem Serial Number" ? order : "asc"}
              onClick={() => handleRequestSort("Modem Serial Number")}
            >
              Modem Serial Number
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Network"}
              direction={orderBy === "Network" ? order : "asc"}
              onClick={() => handleRequestSort("Network")}
            >
              Network
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Controller"}
              direction={orderBy === "Controller" ? order : "asc"}
              onClick={() => handleRequestSort("Controller")}
            >
              Controller
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Plungers"}
              direction={orderBy === "Plungers" ? order : "asc"}
              onClick={() => handleRequestSort("Plungers")}
            >
              Plungers
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "SD Code"}
              direction={orderBy === "SD Code" ? order : "asc"}
              onClick={() => handleRequestSort("SD Code")}
            >
              SD Code
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Shutdown Enum"}
              direction={orderBy === "Shutdown Enum" ? order : "asc"}
              onClick={() => handleRequestSort("Shutdown Enum")}
            >
              Shutdown Enum
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, padding: "8px" }}>
            <TableSortLabel
              active={orderBy === "Sample Time"}
              direction={orderBy === "Sample Time" ? order : "asc"}
              onClick={() => handleRequestSort("Sample Time")}
            >
              Sample Time
            </TableSortLabel>
          </TableCell>

          <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "11px", sm: "14px", md: "15px" }, textAlign: "center", padding: "8px" }}>
            Unit Page
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {paginatedUnits.length > 0 ? (
          paginatedUnits.map((controller, index) => (
            <>
              <TableRow
                key={controller.unitId}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                  "&:hover": { backgroundColor: "#4a4d54" },
                }}
              >
                {/* Expand Button - Mobile Only */}
                <TableCell sx={{ display: { xs: "table-cell", md: "none" }, textAlign: "center" }}>
                  <IconButton
                    size="small"
                    onClick={() => toggleRowExpansion(controller.unitId)}
                    sx={{ color: "white" }}
                  >
                    {expandedRows[controller.unitId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </TableCell>

                <TableCell sx={{ fontSize: { xs: "11px", sm: "14px", md: "15px" } }}>
                  {controller.name}
                </TableCell>

                <TableCell sx={{ fontSize: { xs: "11px", sm: "14px", md: "15px" } }}>
                  {controller.unitId}
                </TableCell>

                <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontSize: { xs: "11px", sm: "14px", md: "15px" } }}>
                  {controller.interface}
                </TableCell>

                {controller.type === "WellPro II" ? (
                  <WellProCells controller={controller} />
                ) : controller.type === "C5" || controller.type === "C5E" ? (
                  <C5Cells controller={controller} />
                ) : (
                  <>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>-</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>-</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>-</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      <Box style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 2 }}>
                        <Typography>MODBUS ⚠️</Typography>
                      </Box>
                    </TableCell>
                  </>
                )}

                <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontSize: { xs: "11px", sm: "14px", md: "15px" } }}>
                  <Box>
                    {formatDate(controller.time * 1000)}
                    {PRC_WELL.getStatus(controller.time) === "ONLINE"
                      ? " ✅"
                      : PRC_WELL.getStatus(controller.time) === "MISSED"
                      ? " ⚠️"
                      : " ⛔"}
                  </Box>
                </TableCell>

                <TableCell sx={{ textAlign: "center", fontSize: { xs: "11px", sm: "14px", md: "15px" } }}>
                  <Button variant="contained" size="small" onClick={() => gotoUnitPage(controller.unitId)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>

              {/* Mobile Collapsed Row */}
              <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={4}
                  sx={{ backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43" }}
                >
                  <Collapse in={expandedRows[controller.unitId]} timeout="auto" unmountOnExit>
                    <Box sx={{ py: 2, px: 1 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: "#999", fontWeight: "bold" }}>
                            Network:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {controller.interface}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: "#999", fontWeight: "bold" }}>
                            Controller:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {controller.controllerName}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: "#999", fontWeight: "bold" }}>
                            Plungers:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {controller.plungers}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: "#999", fontWeight: "bold" }}>
                            SD Code:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {controller.sdCode}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: "#999", fontWeight: "bold" }}>
                            Shutdown Enum:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {controller.sdEnumStatus}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: "#999", fontWeight: "bold" }}>
                            Sample Time:
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {controller.sampleTime}
                            </Typography>
                            <Typography>
                              {PRC_WELL.getStatus(controller.time) === "ONLINE"
                                ? "✅"
                                : PRC_WELL.getStatus(controller.time) === "MISSED"
                                ? "⚠️"
                                : "⛔"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
              No Controllers Found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 0 },
        height: "100%",
        position: "relative",
      }}
    >
      {/* Overlay Loader */}
      {loadingController && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h5" fontWeight="bold">
              Controller Status
            </Typography>
            <Chip
              label={`${controllers.length} units`}
              color="primary"
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              sx={{ borderRadius: 0 }}
              variant="outlined"
              color="white"
              startIcon={<GetAppIcon />}
              onClick={handleExportMenuOpen}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TextField
          label="Search by Name, ID, Network, Controller and SD Code..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Table */}
        {renderTable()}
      </Paper>
      <TablePagination
        component="div"
        count={searchQuery ? filteredControllers.length : controllers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sx={{ mt: 2 }}
      />
      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
      >
        <MenuItem onClick={copyToClipboard}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToCSV}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToExcel}>
          <ListItemIcon>
            <TableViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
